import React, { useRef, useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import './TestPanel.css';
import { usePanels } from '../../../context/PanelsContext';
import InfoTable from '../../shared/InfoTable';
import { generateKolonSemaMultiMeter, clearKolonSema } from '../../../utils/kolonSema/generateKolonSemaMultiMeter';
import { createYuklemeCetvelMultiMeter } from '../../../modules/documents';
import { dxfDrawCore } from '../../../modules/dxf';
import { generateYCDataFromKolonConfig } from '../../../utils/generateYCDataFromKolonConfig';
import { generateYuklemeCetveliXlsx, adaptYCDataToXlsx } from '../../../modules/documents';
import { createOLE2FrameMeshFromXlsx } from '../../../utils/createOLE2FrameMeshFromXlsx';


// Kat seçenekleri oluştur
const normalKatOptions = Array.from({ length: 20 }, (_, i) => `${i + 1}`);
const bodrumKatOptions = ["0", "1", "2", "3", "4", "5", "6"];

const YC_GROUP_NAME = 'yuklemeCetveliMultiMeter';

const TestPanel = ({ scene }) => {
  // Kolon şeması state'leri
  const [kolonConfig, setKolonConfig] = useState({
    normalKat: "5",
    bodrumKat: "1",
    asmaKat: false,
    galeriKat: false,
    catiKat: false,
    girisKati: "Zemin Kat",
    enerjiOdasiKati: "Zemin Kat",
    katBasiDaire: "4",
    anaHatFaz: "3 Faz",
    daireFaz: "1 Faz",
    asansor: true,
    asansorGucu: "15",
    musterek: true,
    musterekGucu: "5",
    hidrofor: false,
    // Topraklama (E.P.B.) seçenekleri
    topraklamaMetalSu: false,
    topraklamaMetalGaz: false,
    topraklamaAsansorRay: true,
    topraklamaKabloMerdiven: false,
    // TMS Şalter
    tmsSimetrik: false,
    tmsTroidBobin: false,
    tmsAcilAcma: false,
    anaKolonFazi: "R",
    // Genel Tablo (G.T.)
    gtMerdivenCikis: "3",     // merdiven aydınlatma çıkış sayısı (1-8)
    gtAsansorCikis: "1",      // asansör aydınlatma çıkış sayısı (1-8)
    gtMerdivenArmatur: "4",   // merdiven armatür sayısı (1-9)
    gtAsansorArmatur: "4",    // asansör armatür sayısı (1-9)
    gtMerdivenKatlar: {},     // her çıkış için hangi katlar: { "0": { "5.Normal Kat": true, ... }, ... }
    gtAsansorKatlar: {},      // her çıkış için hangi katlar
    gtConfig: {},
    katlar: [],
    anaKolonHatti: { iletken: 44, kesici: 555 },
  });

  // Tüm katları sıralı olarak oluştur (aşağıdan yukarıya)
  const tumKatlar = useMemo(() => {
    const katlar = [];
    const bodrumSayisi = parseInt(kolonConfig.bodrumKat) || 0;
    const normalSayisi = parseInt(kolonConfig.normalKat) || 0;

    // Bodrum katları (en derinden yukarıya): -N.Bodrum, ..., -1.Bodrum
    for (let i = bodrumSayisi; i >= 1; i--) {
      katlar.push(`-${i}.Bodrum Kat`);
    }

    // Zemin kat
    katlar.push("Zemin Kat");

    // Asma kat (varsa)
    if (kolonConfig.asmaKat) {
      katlar.push("Asma Kat");
    }

    // Galeri kat (varsa)
    if (kolonConfig.galeriKat) {
      katlar.push("Galeri Kat");
    }

    // Normal katlar: 1.Normal, 2.Normal, ..., N.Normal
    for (let i = 1; i <= normalSayisi; i++) {
      katlar.push(`${i}.Normal Kat`);
    }

    // Çatı katı (varsa)
    if (kolonConfig.catiKat) {
      katlar.push("Çatı Kat");
    }

    return katlar;
  }, [kolonConfig.bodrumKat, kolonConfig.normalKat, kolonConfig.asmaKat, kolonConfig.galeriKat, kolonConfig.catiKat]);

  // tumKatlar veya katBasiDaire değişince katlar dizisini yeniden oluştur
  useEffect(() => {
    const katBasiDaire = parseInt(kolonConfig.katBasiDaire) || 1;
    const yeniKatlar = tumKatlar.map((katName, idx) => ({
      name: katName,
      katNo: idx,
      katType: "normal",
      paneller: Array.from({ length: katBasiDaire }, (_, pi) => ({
        name: `${katName}-${pi + 1}`,
        sayacKesici: 423,
        panelKesici: 46,
        iletken: 44,
        mesafe: 12,
        guc: 14800,
        faz: "R",
        aboneTipi: "mesken",
        kullanimAmaci: "mesken"
      }))
    }));
    setKolonConfig(prev => {
      const panelsPerFloor = {};
      yeniKatlar.forEach(k => { panelsPerFloor[k.name] = k.paneller.length; });
      return { ...prev, katlar: yeniKatlar, panelsPerFloor };
    });
  }, [tumKatlar, kolonConfig.katBasiDaire]);

  // Genel Tablo (G.T.) config'ini tumKatlar değişince güncelle
  useEffect(() => {
    setKolonConfig(prev => {
      const yeniGtConfig = {};
      tumKatlar.forEach(kat => {
        yeniGtConfig[kat] = prev.gtConfig?.[kat] || {
          asansor: false,
          kazanDairesi: false,
          siginak: false,
          jenerator: false,
          otopark: false,
          mustemilat: false,
          yanginIhbar: false,
        };
      });
      return { ...prev, gtConfig: yeniGtConfig };
    });
  }, [tumKatlar]);

  // GT Merdiven/Asansör katlar config'ini çıkış sayısı veya tumKatlar değişince güncelle
  useEffect(() => {
    setKolonConfig(prev => {
      const merdivenCikis = parseInt(prev.gtMerdivenCikis) || 3;
      const asansorCikis = parseInt(prev.gtAsansorCikis) || 1;

      // Katları yukarıdan aşağıya sırala (dağıtım için)
      const katlarYukaridan = [...tumKatlar].reverse();

      // Merdiven: Katları çıkışlara eşit dağıt (yukarıdan aşağıya)
      const yeniMerdivenKatlar = {};
      const mevcutMerdiven = prev.gtMerdivenKatlar || {};
      // Eğer mevcut config çıkış sayısıyla uyumluysa koru
      const mevcutMerdivenCount = Object.keys(mevcutMerdiven).length;
      if (mevcutMerdivenCount === merdivenCikis && Object.values(mevcutMerdiven).some(k => Object.keys(k).length > 0)) {
        // Mevcut config'i koru ama yeni katları ekle/eskileri temizle
        for (let i = 0; i < merdivenCikis; i++) {
          const mevcutKatlar = mevcutMerdiven[String(i)] || {};
          yeniMerdivenKatlar[String(i)] = {};
          tumKatlar.forEach(kat => {
            yeniMerdivenKatlar[String(i)][kat] = mevcutKatlar[kat] || false;
          });
        }
      } else {
        // Yeni default oluştur: katları eşit dağıt (yukarıdan aşağıya)
        const katPerCikis = Math.ceil(katlarYukaridan.length / merdivenCikis);
        for (let i = 0; i < merdivenCikis; i++) {
          yeniMerdivenKatlar[String(i)] = {};
          tumKatlar.forEach(kat => { yeniMerdivenKatlar[String(i)][kat] = false; });
          const baslangic = i * katPerCikis;
          const bitis = Math.min(baslangic + katPerCikis, katlarYukaridan.length);
          for (let k = baslangic; k < bitis; k++) {
            yeniMerdivenKatlar[String(i)][katlarYukaridan[k]] = true;
          }
        }
      }

      // Asansör: aynı mantık
      const yeniAsansorKatlar = {};
      const mevcutAsansor = prev.gtAsansorKatlar || {};
      const mevcutAsansorCount = Object.keys(mevcutAsansor).length;
      if (mevcutAsansorCount === asansorCikis && Object.values(mevcutAsansor).some(k => Object.keys(k).length > 0)) {
        for (let i = 0; i < asansorCikis; i++) {
          const mevcutKatlar = mevcutAsansor[String(i)] || {};
          yeniAsansorKatlar[String(i)] = {};
          tumKatlar.forEach(kat => {
            yeniAsansorKatlar[String(i)][kat] = mevcutKatlar[kat] || false;
          });
        }
      } else {
        // Default: tüm katlar tüm çıkışlara
        const katPerCikis = Math.ceil(katlarYukaridan.length / asansorCikis);
        for (let i = 0; i < asansorCikis; i++) {
          yeniAsansorKatlar[String(i)] = {};
          tumKatlar.forEach(kat => { yeniAsansorKatlar[String(i)][kat] = false; });
          const baslangic = i * katPerCikis;
          const bitis = Math.min(baslangic + katPerCikis, katlarYukaridan.length);
          for (let k = baslangic; k < bitis; k++) {
            yeniAsansorKatlar[String(i)][katlarYukaridan[k]] = true;
          }
        }
      }

      return { ...prev, gtMerdivenKatlar: yeniMerdivenKatlar, gtAsansorKatlar: yeniAsansorKatlar };
    });
  }, [tumKatlar, kolonConfig.gtMerdivenCikis, kolonConfig.gtAsansorCikis]);

  // GT toplam çıkış sayısı
  const gtToplamCikis = useMemo(() => {
    const merdiven = parseInt(kolonConfig.gtMerdivenCikis) || 0;
    const asansor = parseInt(kolonConfig.gtAsansorCikis) || 0;
    // jenerator hariç tüm true checkbox'ları say
    const checkboxKeys = ['asansor', 'kazanDairesi', 'siginak', 'otopark', 'mustemilat', 'yanginIhbar'];
    let checkboxToplam = 0;
    if (kolonConfig.gtConfig) {
      Object.values(kolonConfig.gtConfig).forEach(katConfig => {
        checkboxKeys.forEach(key => {
          if (katConfig[key]) checkboxToplam++;
        });
      });
    }
    // +1 zayıf akım
    return merdiven + asansor + checkboxToplam + 1;
  }, [kolonConfig.gtMerdivenCikis, kolonConfig.gtAsansorCikis, kolonConfig.gtConfig]);

  // Enerji odası seçenekleri (giriş katından ±1 kat uzaklıkta)
  const enerjiOdasiSecenekleri = useMemo(() => {
    const girisIndex = tumKatlar.indexOf(kolonConfig.girisKati);
    if (girisIndex === -1) return [tumKatlar[0] || "Zemin Kat"];

    const secenekler = [];
    // Bir alt kat
    if (girisIndex > 0) {
      secenekler.push(tumKatlar[girisIndex - 1]);
    }
    // Giriş katının kendisi
    secenekler.push(tumKatlar[girisIndex]);
    // Bir üst kat
    if (girisIndex < tumKatlar.length - 1) {
      secenekler.push(tumKatlar[girisIndex + 1]);
    }

    return secenekler;
  }, [tumKatlar, kolonConfig.girisKati]);

  // Asma/Galeri kat değişikliği (birbirini exclude eder)
  const handleAsmaGaleriChange = (type) => {
    setKolonConfig(prev => {
      const newConfig = { ...prev };
      if (type === 'asma') {
        newConfig.asmaKat = !prev.asmaKat;
        newConfig.galeriKat = false;
      } else {
        newConfig.galeriKat = !prev.galeriKat;
        newConfig.asmaKat = false;
      }
      return newConfig;
    });
  };

  // Genel config değişikliği
  const handleConfigChange = (key, value) => {
    setKolonConfig(prev => ({ ...prev, [key]: value }));
  };

  // GT merdiven/asansör kat checkbox handler'ı
  const handleGtAydinlatmaKatChange = (tip, cikisIdx, kat) => {
    const configKey = tip === 'merdiven' ? 'gtMerdivenKatlar' : 'gtAsansorKatlar';
    setKolonConfig(prev => ({
      ...prev,
      [configKey]: {
        ...prev[configKey],
        [String(cikisIdx)]: {
          ...prev[configKey]?.[String(cikisIdx)],
          [kat]: !prev[configKey]?.[String(cikisIdx)]?.[kat]
        }
      }
    }));
  };

  // GT config değiştirme handler'ı
  const handleGtConfigChange = (kat, key, value) => {
    setKolonConfig(prev => ({
      ...prev,
      gtConfig: {
        ...prev.gtConfig,
        [kat]: { ...prev.gtConfig[kat], [key]: value }
      }
    }));
  };

  // Giriş katı değiştiğinde enerji odası katını da güncelle
  const handleGirisKatiChange = (value) => {
    setKolonConfig(prev => {
      const newConfig = { ...prev, girisKati: value };
      // Enerji odası seçeneklerini yeniden hesapla
      const girisIndex = tumKatlar.indexOf(value);
      if (girisIndex !== -1) {
        // Varsayılan olarak giriş katını seç
        newConfig.enerjiOdasiKati = value;
      }
      return newConfig;
    });
  };

  // Giriş katı geçerli mi kontrol et, değilse düzelt
  useEffect(() => {
    if (!tumKatlar.includes(kolonConfig.girisKati)) {
      const yeniGiris = tumKatlar.includes("Zemin Kat") ? "Zemin Kat" : tumKatlar[0];
      if (yeniGiris) {
        setKolonConfig(prev => ({ ...prev, girisKati: yeniGiris, enerjiOdasiKati: yeniGiris }));
      }
    }
  }, [tumKatlar, kolonConfig.girisKati]);

  // Enerji odası katı geçerli mi kontrol et
  useEffect(() => {
    if (!enerjiOdasiSecenekleri.includes(kolonConfig.enerjiOdasiKati)) {
      setKolonConfig(prev => ({ ...prev, enerjiOdasiKati: enerjiOdasiSecenekleri[0] || prev.girisKati }));
    }
  }, [enerjiOdasiSecenekleri, kolonConfig.enerjiOdasiKati]);

  // Kolon şemasını sahneye çiz (test amaçlı)
  const handleKolonSemaOlustur = () => {
    if (!scene) {
      console.warn('[TestPanel] Scene objesi yok');
      return;
    }
    clearKolonSema(scene);

    generateKolonSemaMultiMeter(scene, kolonConfig, tumKatlar, { centerX: 0, centerY: 0 });
  };

  // Yükleme cetvelini OLE2FRAME olarak sahneye ekle (test amaçlı)
  const handleYukCetveliOleEkle = async () => {
    if (!scene) {
      console.warn('[TestPanel] Scene objesi yok');
      return;
    }
    try {
      // Önceki test OLE'lerini temizle
      const oldOles = [];
      scene.traverse(c => {
        if (c.userData?.source === 'dxf' && c.userData?.type === 'ole2frame' &&
            c.userData?.testTag === 'yukCetveliOle') oldOles.push(c);
      });
      oldOles.forEach(o => { o.parent?.remove(o); o.geometry?.dispose?.(); o.material?.map?.dispose?.(); o.material?.dispose?.(); });

      const ycData = generateYCDataFromKolonConfig(kolonConfig, tumKatlar);
      const xlsxData = adaptYCDataToXlsx(ycData);
      const xlsxBytes = await generateYuklemeCetveliXlsx(xlsxData);

      const W = 1050, H = 600; // Geçici sabit boyut — sonra layout entegrasyonunda hesaplanacak
      const mesh = await createOLE2FrameMeshFromXlsx({
        xlsxBytes,
        upperLeft:  { x: 0,    y: 0,  z: 0 },
        lowerRight: { x: W,    y: -H, z: 0 },
        layerName: '0',
      });
      mesh.userData.testTag = 'yukCetveliOle';
      scene.add(mesh);
    } catch (err) {
      console.error('[TestPanel] OLE üretiminde hata:', err);
      alert('OLE üretiminde hata: ' + (err?.message || err));
    }
  };

  // Yükleme cetvelini xlsx olarak indir (test amaçlı)
  const handleYukCetveliXlsxIndir = async () => {
    try {
      const ycData = generateYCDataFromKolonConfig(kolonConfig, tumKatlar);
      const xlsxData = adaptYCDataToXlsx(ycData);
      const buf = await generateYuklemeCetveliXlsx(xlsxData);
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yukleme-cetveli.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[TestPanel] yükleme cetveli xlsx üretiminde hata:', err);
      alert('Yükleme cetveli xlsx üretiminde hata: ' + (err?.message || err));
    }
  };

  // kolonConfig veya tumKatlar değişince otomatik güncelle
  // useEffect(() => {
  //   if (!scene) return;
  //   clearKolonSema(scene);
  //   generateKolonSemaMultiMeter(scene, kolonConfig, tumKatlar, { centerX: 0, centerY: 0 });
  // }, [scene, kolonConfig, tumKatlar]);

  // Toplam sayaç hesapla
  const toplamSayac = () => {
    const katBasiDaire = parseInt(kolonConfig.katBasiDaire) || 0;
    // Tüm katlar listesinden sayaç hesapla (her katta daire var)
    return tumKatlar.length * katBasiDaire;
  };

  // Kolon Şeması Yapılandırma Verileri
  const data = [
    // Bina Genel Bilgileri - Kat Yapısı
    { label: "Normal Kat Sayısı", type: "select", value: kolonConfig.normalKat, options: normalKatOptions, onChange: (e) => handleConfigChange('normalKat', e.target.value) },
    { label: "Bodrum Kat Sayısı", type: "select", value: kolonConfig.bodrumKat, options: bodrumKatOptions, onChange: (e) => handleConfigChange('bodrumKat', e.target.value) },
    { label: "Asma Kat", type: "checkbox", value: kolonConfig.asmaKat, onChange: () => handleAsmaGaleriChange('asma') },
    { label: "Galeri Kat", type: "checkbox", value: kolonConfig.galeriKat, onChange: () => handleAsmaGaleriChange('galeri') },
    { label: "Çatı Katı", type: "checkbox", value: kolonConfig.catiKat, onChange: () => handleConfigChange('catiKat', !kolonConfig.catiKat) },

    // Giriş ve Enerji Odası
    { label: "Giriş Katı", type: "select", value: kolonConfig.girisKati, options: tumKatlar, onChange: (e) => handleGirisKatiChange(e.target.value) },
    { label: "Enerji Odası Katı", type: "select", value: kolonConfig.enerjiOdasiKati, options: enerjiOdasiSecenekleri, onChange: (e) => handleConfigChange('enerjiOdasiKati', e.target.value) },

    // Sayaç Bilgileri
    { label: "Kat Başı Daire", type: "number", value: kolonConfig.katBasiDaire, min: 1, max: 10, onChange: (e) => handleConfigChange('katBasiDaire', e.target.value) },
    { label: "Toplam Sayaç", type: "text", value: toplamSayac().toString(), disabled: true },

    // Faz Yapılandırması
    { label: "Ana Hat Faz", type: "select", value: kolonConfig.anaHatFaz, options: ["1 Faz", "3 Faz"], onChange: (e) => handleConfigChange('anaHatFaz', e.target.value) },
    { label: "Daire Faz", type: "select", value: kolonConfig.daireFaz, options: ["1 Faz", "3 Faz"], onChange: (e) => handleConfigChange('daireFaz', e.target.value) },

    // Özel Panolar
    { label: "Asansör", type: "checkbox", value: kolonConfig.asansor, onChange: () => handleConfigChange('asansor', !kolonConfig.asansor) },
    { label: "Asansör Gücü (kW)", type: "number", value: kolonConfig.asansorGucu, min: 5, max: 50, onChange: (e) => handleConfigChange('asansorGucu', e.target.value) },
    { label: "Müşterek", type: "checkbox", value: kolonConfig.musterek, onChange: () => handleConfigChange('musterek', !kolonConfig.musterek) },
    { label: "Müşterek Gücü (kW)", type: "number", value: kolonConfig.musterekGucu, min: 1, max: 20, onChange: (e) => handleConfigChange('musterekGucu', e.target.value) },
    { label: "Hidrofor", type: "checkbox", value: kolonConfig.hidrofor, onChange: () => handleConfigChange('hidrofor', !kolonConfig.hidrofor) },

    // Bilgi
    { label: "Şema Durumu", type: "text", value: scene ? "Otomatik Güncelleniyor" : "Sahne Bekleniyor", disabled: true },

    // İşlemler
    { label: "Config Göster", type: "button", value: "Console'a Yaz", onClick: () => { console.log('[TestPanel] kolonConfig:', JSON.parse(JSON.stringify(kolonConfig))); console.log('[TestPanel] tumKatlar:', tumKatlar); console.log('[TestPanel] katlar:', JSON.parse(JSON.stringify(kolonConfig.katlar))); } },
    { label: "Kolon Şeması", type: "button", value: "Oluştur", onClick: handleKolonSemaOlustur },
    { label: "Yükleme Cetveli XLSX", type: "button", value: "İndir", onClick: handleYukCetveliXlsxIndir },
    { label: "Yükleme Cetveli OLE", type: "button", value: "Sahneye Ekle", onClick: handleYukCetveliOleEkle },
  ];
  const ref = useRef(null);
  const headerRef = useRef(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 350 });
  const startSize = useRef({ width: 0, height: 0 });

  const [collapsed, setCollapsed] = useState(true);
  const [infoHeight, setInfoHeight] = useState(400);
  const [topraklamaOpen, setTopraklamaOpen] = useState(false);
  const [anaKolonOpen, setAnaKolonOpen] = useState(false);
  const [genelTabloOpen, setGenelTabloOpen] = useState(false);

  const TOPBAR_HEIGHT = 115;
  const SNAP_THRESHOLD = 30;

  const { registerPanel, updatePanelPosition, panels, setPanelHeight, getStackTop, activeTabId } = usePanels();
  const wasDragged = useRef(false);

  const toggle = () => setCollapsed(prev => !prev);

  const showSnapHighlight = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('snap-highlight');
    setTimeout(() => el.classList.remove('snap-highlight'), 300);
  };

  // Register panel on mount
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    registerPanel("TestPanel", {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  // Collapse/expand değişince boyut güncelle + stack height bildir
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      updatePanelPosition("TestPanel", {
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height, collapsed,
      });
      setPanelHeight("TestPanel", collapsed ? 40 : rect.height);
    });
  }, [collapsed]);

  // RESTORE — yalnız PAFTA değişince: konum + açık/kapalı geri yükle. getStackTop'a
  // bağlı DEĞİL → açma/kapama bu effect'i tetiklemez.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stored = panels["TestPanel"];
    if (stored?.dragged) {
      el.style.left = `${stored.left}px`;
      el.style.top = `${stored.top}px`;
      wasDragged.current = true;
    } else {
      wasDragged.current = false;
      const stackTop = getStackTop("TestPanel");
      el.style.left = '';
      el.style.top = `${stackTop}px`;
      el.style.maxHeight = `${window.innerHeight - stackTop}px`;
    }
    const wantCollapsed = stored && typeof stored.collapsed === 'boolean' ? stored.collapsed : true;
    if (wantCollapsed !== collapsed) setCollapsed(wantCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Pafta-İÇİ yeniden stack (sürüklenmemiş paneller)
  useEffect(() => {
    const el = ref.current;
    if (!el || wasDragged.current) return;
    const stackTop = getStackTop("TestPanel");
    el.style.top = `${stackTop}px`;
    el.style.maxHeight = `${window.innerHeight - stackTop}px`;
  }, [getStackTop]);

  useEffect(() => {
    const el = ref.current;
    const header = headerRef.current;

    const onMouseDown = (e) => {
      if (isResizing.current) return;
      isDragging.current = true;
      offset.current = {
        x: e.clientX - el.offsetLeft,
        y: e.clientY - el.offsetTop,
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;

      let x = e.clientX - offset.current.x;
      let y = e.clientY - offset.current.y;
      const ww = window.innerWidth;
      const wh = window.innerHeight;
      const w = el.offsetWidth;
      const h = el.offsetHeight;

      // Snap to window
      if (x < SNAP_THRESHOLD) {
        x = 0;
        showSnapHighlight();
      }
      if (x + w > ww - SNAP_THRESHOLD) {
        x = ww - w;
        showSnapHighlight();
      }
      if (y < TOPBAR_HEIGHT + SNAP_THRESHOLD) {
        y = TOPBAR_HEIGHT;
        showSnapHighlight();
      }

      // Snap to other panels
      Object.entries(panels).forEach(([key, p]) => {
        if (key === "TestPanel") return;

        if (Math.abs(x - (p.left + p.width)) < SNAP_THRESHOLD &&
            Math.abs(y - p.top) < p.height) {
          x = p.left + p.width;
          y = p.top;
          showSnapHighlight();
        }

        if (Math.abs(x + w - p.left) < SNAP_THRESHOLD &&
            Math.abs(y - p.top) < p.height) {
          x = p.left - w;
          y = p.top;
          showSnapHighlight();
        }

        if (Math.abs(y - (p.top + p.height)) < SNAP_THRESHOLD &&
            Math.abs(x - p.left) < p.width) {
          y = p.top + p.height;
          x = p.left;
          showSnapHighlight();
        }

        if (Math.abs(y + h - p.top) < SNAP_THRESHOLD &&
            Math.abs(x - p.left) < p.width) {
          y = p.top - h;
          x = p.left;
          showSnapHighlight();
        }
      });

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.maxHeight = `${window.innerHeight - y}px`;
      wasDragged.current = true;
      updatePanelPosition("TestPanel", { top: y, left: x, dragged: true });
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
    return () => header.removeEventListener('mousedown', onMouseDown);
  }, [panels]);

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    const rect = ref.current.getBoundingClientRect();
    startSize.current = { width: rect.width, height: rect.height<infoHeight?rect.height:infoHeight };
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleResizing = (e) => {
    if (!isResizing.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;

    const newWidth = startSize.current.width + dx;
    const newHeight = startSize.current.height + dy;
    

    if (newWidth > 200) ref.current.style.width = `${newWidth}px`;
    if (newHeight < infoHeight) ref.current.style.height = `${newHeight}px`;
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', stopResizing);
  };
const handleHeightChange=(newData)=>{
  setInfoHeight(newData);
}
  return (
    <div
      ref={ref}
      className={`TestPanel ${collapsed ? 'collapsed' : ''}`}
      style={{
        height: collapsed ? '40px' : undefined,
      }}
    >
      <div className="TestPanel-header" ref={headerRef}>
        <span onClick={toggle}>Kolon Şeması Paneli</span>
        <span style={{ fontSize: '18px', cursor: 'pointer' }} onClick={toggle}>
          {collapsed ? '▼' : '▲'}
        </span>
      </div>
      <div className="TestPanel-content">
        <InfoTable data={data} onHeightChange={handleHeightChange} />
        {/* Topraklama (E.P.B.) Akordiyonu */}
        <div className="accordion-section">
          <div className="accordion-header" onClick={() => setTopraklamaOpen(prev => !prev)}>
            <span>Topraklama (E.P.B.)</span>
            <span className="accordion-arrow">{topraklamaOpen ? '▲' : '▼'}</span>
          </div>
          {topraklamaOpen && (
            <div className="accordion-body">
              <table className="info-table-grid">
                <tbody>
                  <tr>
                    <td className="info-label">Metal Su Boruları</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.topraklamaMetalSu}
                        onChange={() => handleConfigChange('topraklamaMetalSu', !kolonConfig.topraklamaMetalSu)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Metal Gaz Boruları</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.topraklamaMetalGaz}
                        onChange={() => handleConfigChange('topraklamaMetalGaz', !kolonConfig.topraklamaMetalGaz)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Asansör Rayları</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.topraklamaAsansorRay}
                        onChange={() => handleConfigChange('topraklamaAsansorRay', !kolonConfig.topraklamaAsansorRay)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Kablo Merdiveni</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.topraklamaKabloMerdiven}
                        onChange={() => handleConfigChange('topraklamaKabloMerdiven', !kolonConfig.topraklamaKabloMerdiven)} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Ana Kolon Hattı Ayarları */}
        <div className="accordion-section">
          <div className="accordion-header" onClick={() => setAnaKolonOpen(prev => !prev)}>
            <span>Ana Kolon Hattı</span>
            <span className="accordion-arrow">{anaKolonOpen ? '▲' : '▼'}</span>
          </div>
          {anaKolonOpen && (
            <div className="accordion-body">
              <table className="info-table-grid">
                <tbody>
                  <tr>
                    <td className="info-label">TMS Y Simetrik</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.tmsSimetrik}
                        onChange={() => handleConfigChange('tmsSimetrik', !kolonConfig.tmsSimetrik)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Troid Bobin</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.tmsTroidBobin}
                        onChange={() => handleConfigChange('tmsTroidBobin', !kolonConfig.tmsTroidBobin)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Acil Açma Butonu</td>
                    <td className="info-value">
                      <input type="checkbox" className="info-checkbox"
                        checked={kolonConfig.tmsAcilAcma}
                        onChange={() => handleConfigChange('tmsAcilAcma', !kolonConfig.tmsAcilAcma)} />
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Ana Kolon Fazı</td>
                    <td className="info-value">
                      <select className="info-select"
                        value={kolonConfig.anaKolonFazi}
                        onChange={(e) => handleConfigChange('anaKolonFazi', e.target.value)}>
                        <option value="R">R</option>
                        <option value="S">S</option>
                        <option value="T">T</option>
                        <option value="RST">RST</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Genel Tablo (G.T.) Akordiyonu */}
        <div className="accordion-section">
          <div className="accordion-header" onClick={() => setGenelTabloOpen(prev => !prev)}>
            <span>Genel Tablo (G.T.)</span>
            <span className="accordion-arrow">{genelTabloOpen ? '▲' : '▼'}</span>
          </div>
          {genelTabloOpen && (
            <div className="accordion-body">
              <table className="info-table-grid" style={{ marginBottom: 10 }}>
                <tbody>
                  <tr>
                    <td className="info-label">Merdiven Aydınlatma Çıkışı</td>
                    <td className="info-value">
                      <select className="info-select"
                        value={kolonConfig.gtMerdivenCikis}
                        onChange={(e) => handleConfigChange('gtMerdivenCikis', e.target.value)}>
                        {[1,2,3,4,5,6,7,8].map(n => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Asansör Aydınlatma Çıkışı</td>
                    <td className="info-value">
                      <select className="info-select"
                        value={kolonConfig.gtAsansorCikis}
                        onChange={(e) => handleConfigChange('gtAsansorCikis', e.target.value)}>
                        {[1,2,3,4,5,6,7,8].map(n => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Merdiven Armatür Sayısı</td>
                    <td className="info-value">
                      <select className="info-select"
                        value={kolonConfig.gtMerdivenArmatur || "4"}
                        onChange={(e) => handleConfigChange('gtMerdivenArmatur', e.target.value)}>
                        {[1,2,3,4,5,6,7,8,9].map(n => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Asansör Armatür Sayısı</td>
                    <td className="info-value">
                      <select className="info-select"
                        value={kolonConfig.gtAsansorArmatur || "4"}
                        onChange={(e) => handleConfigChange('gtAsansorArmatur', e.target.value)}>
                        {[1,2,3,4,5,6,7,8,9].map(n => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="info-label">Toplam Çıkış</td>
                    <td className="info-value">{gtToplamCikis}</td>
                  </tr>
                </tbody>
              </table>
              {/* Merdiven Aydınlatma çıkışları — her çıkış için kat checkboxları */}
              {Array.from({ length: parseInt(kolonConfig.gtMerdivenCikis) || 3 }, (_, ci) => (
                <div key={`merdiven_${ci}`} style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid #555', padding: '3px 0' }}>
                  <span style={{ fontWeight: 'bold', minWidth: 120, fontSize: 11 }}>{ci + 1}. Mrd. Ayd.</span>
                  {[...tumKatlar].reverse().map(kat => (
                    <input key={kat} type="checkbox" className="info-checkbox"
                      title={kat}
                      checked={kolonConfig.gtMerdivenKatlar?.[String(ci)]?.[kat] || false}
                      onChange={() => handleGtAydinlatmaKatChange('merdiven', ci, kat)} />
                  ))}
                </div>
              ))}
              {/* Asansör Aydınlatma çıkışları — her çıkış için kat checkboxları */}
              {Array.from({ length: parseInt(kolonConfig.gtAsansorCikis) || 1 }, (_, ci) => (
                <div key={`asansor_${ci}`} style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid #555', padding: '3px 0' }}>
                  <span style={{ fontWeight: 'bold', minWidth: 120, fontSize: 11 }}>{ci + 1}. Asn. Ayd.</span>
                  {[...tumKatlar].reverse().map(kat => (
                    <input key={kat} type="checkbox" className="info-checkbox"
                      title={kat}
                      checked={kolonConfig.gtAsansorKatlar?.[String(ci)]?.[kat] || false}
                      onChange={() => handleGtAydinlatmaKatChange('asansor', ci, kat)} />
                  ))}
                </div>
              ))}
              {tumKatlar.map(kat => (
                <div key={kat} style={{ display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #444', padding: '4px 0' }}>
                  <span style={{ fontWeight: 'bold', minWidth: 90 }}>{kat}</span>
                  {[
                    { key: 'asansor', label: 'Asansör' },
                    { key: 'kazanDairesi', label: 'Kazan Dairesi' },
                    { key: 'siginak', label: 'Sığınak' },
                    { key: 'jenerator', label: 'Jeneratör' },
                    { key: 'otopark', label: 'Otopark' },
                    { key: 'mustemilat', label: 'Müştemilat' },
                    { key: 'yanginIhbar', label: 'Yangın İhbar Santrali' },
                  ].map(item => (
                    <input key={item.key} type="checkbox" className="info-checkbox"
                      title={item.label}
                      checked={kolonConfig.gtConfig?.[kat]?.[item.key] || false}
                      onChange={() => handleGtConfigChange(kat, item.key, !kolonConfig.gtConfig?.[kat]?.[item.key])} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="resize-handle" onMouseDown={handleResizeMouseDown} />
    </div>
  );
};

export default TestPanel;
